/**
 * LoginPage — admin authentication.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Sparkles, Copy, Check } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/app/providers/AuthProvider';
import { fetchPublicConfig } from '@/services/auth/authService';
import { formatApiError } from '@/services/api/client';
import { LOGIN } from '@/constants/testIds';
import brandSymbol from '@/assets/brand/vivilazona-symbol.svg';

export default function LoginPage() {
	const { login } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [copied, setCopied] = useState(false);
	const [demo, setDemo] = useState(null); // { email, password } when demo mode is on (server)

	useEffect(() => {
		fetchPublicConfig()
			.then((cfg) => setDemo(cfg.demo_mode ? cfg.demo_account : null))
			.catch(() => setDemo(null));
	}, []);

	const from = location.state?.from?.pathname || '/admin';

	const onSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setSubmitting(true);
		try {
			const u = await login({ email, password });
			toast.success(`Benvenuto, ${u.full_name}`);
			navigate(from, { replace: true });
		} catch (err) {
			const status = err.response?.status;
			const detail = err.response?.data?.detail;
			const msg = status === 401
				? 'Email o password non corretti.'
				: (detail ? formatApiError(detail) : 'Accesso non riuscito. Riprova.');
			setError(msg);
			toast.error(msg);
		} finally {
			setSubmitting(false);
		}
	};

	const fillDemo = () => {
		if (!demo) return;
		setEmail(demo.email);
		setPassword(demo.password);
		setError('');
	};

	const copyDemo = async () => {
		if (!demo) return;
		try {
			await navigator.clipboard.writeText(`${demo.email} / ${demo.password}`);
			setCopied(true);
			toast.success('Credenziali copiate');
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error('Impossibile copiare');
		}
	};

	return (
		<div
			className="flex min-h-screen items-center justify-center bg-[#0b1120] px-6 text-slate-100"
			style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
		>
			<div className="pointer-events-none fixed -top-40 left-1/2 h-80 w-[42rem] -translate-x-1/2 rounded-full bg-emerald-500/20 blur-[120px]" />
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				className="relative w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/60 p-8 backdrop-blur"
			>
				<div className="mb-6 flex items-center gap-3">
					<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-400/15 ring-1 ring-emerald-400/30">
						<img src={brandSymbol} alt="ViviLaZona" className="h-6 w-6" />
					</div>
					<div>
						<p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-300/80">ViviLaZona</p>
						<h1 className="text-xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>
							Accesso amministrativo
						</h1>
					</div>
				</div>

				<form onSubmit={onSubmit} className="space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="email" className="text-slate-300">Email</Label>
						<Input
							id="email" type="email" required value={email}
							onChange={(e) => setEmail(e.target.value)}
							data-testid={LOGIN.emailInput}
							className="bg-slate-950/50 border-slate-700 text-slate-100"
							placeholder="admin@portosantelpidio.it"
						/>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="password" className="text-slate-300">Password</Label>
						<Input
							id="password" type="password" required value={password}
							onChange={(e) => setPassword(e.target.value)}
							data-testid={LOGIN.passwordInput}
							className="bg-slate-950/50 border-slate-700 text-slate-100"
							placeholder="••••••••"
						/>
					</div>
					{error && (
						<p className="text-sm text-rose-400" data-testid="login-error">{error}</p>
					)}
					<Button
						type="submit" disabled={submitting}
						data-testid={LOGIN.submitButton}
						className="w-full bg-emerald-400 text-slate-950 hover:bg-emerald-300"
					>
						<LogIn size={16} className="mr-2" />
						{submitting ? 'Accesso…' : 'Entra'}
					</Button>
				</form>

				{demo && (
					<div data-testid="demo-credentials-section" className="mt-6 rounded-xl border border-emerald-400/25 bg-emerald-400/5 p-4">
						<div className="mb-2 flex items-center gap-2 text-emerald-300">
							<Sparkles size={15} />
							<p className="text-xs font-semibold uppercase tracking-widest">Accesso demo</p>
						</div>
						<p className="text-xs text-slate-400">Account amministratore di Porto Sant'Elpidio</p>
						<div className="mt-3 space-y-1 rounded-lg bg-slate-950/50 p-3 font-mono text-xs text-slate-200">
							<p data-testid="demo-email"><span className="text-slate-500">email: </span>{demo.email}</p>
							<p data-testid="demo-password"><span className="text-slate-500">password: </span>{demo.password}</p>
						</div>
						<div className="mt-3 flex gap-2">
							<Button
								type="button" onClick={fillDemo}
								data-testid="fill-demo-credentials-button"
								className="flex-1 bg-emerald-400 text-slate-950 hover:bg-emerald-300"
							>
								Compila credenziali
							</Button>
							<Button
								type="button" variant="outline" onClick={copyDemo}
								data-testid="copy-demo-credentials-button"
								className="border-slate-600 text-slate-300 hover:bg-slate-800"
							>
								{copied ? <Check size={15} className="text-emerald-300" /> : <Copy size={15} />}
							</Button>
						</div>
					</div>
				)}

				<Link to="/" className="mt-6 block text-center text-xs text-slate-500 hover:text-emerald-300">
					← Torna allo stato del sistema
				</Link>
			</motion.div>
		</div>
	);
}
